import { Fragment } from "react";
import FadeUp from "./FadeUp";
import {
    ScanSearch,
    UserCheck,
    LineChart,
    ListOrdered,
    Send,
    Wallet,
    FlaskConical,
    Users,
    RefreshCw,
    ChevronRight,
    ChevronDown,
    ArrowRight,
    ArrowDown,
} from "lucide-react";

const steps = [
    {
        n: "01",
        icon: ScanSearch,
        title: "Intelligent Creator Discovery",
        body: "Our algorithm scores audience relevance, views, engagement quality, geography, niche fit, and past content performance.",
    },
    {
        n: "02",
        icon: UserCheck,
        title: "Human Review",
        body: "Our team manually validates audience quality, authenticity, and brand fit, catching what automated analysis misses.",
    },
    {
        n: "03",
        icon: LineChart,
        title: "Predictive Performance Model",
        body: "We estimate cost, trials or demos, and customers for every creator before any outreach.",
        focal: true,
    },
    {
        n: "04",
        icon: ListOrdered,
        title: "ROI-Based Prioritisation",
        body: "Creators are ranked by projected economics and audience fit, not by follower count.",
    },
    {
        n: "05",
        icon: Send,
        title: "Strategic Outreach",
        body: "Only after the analysis and human review do we approach creators and negotiate.",
    },
];

const metrics = [
    {
        icon: Wallet,
        label: "Estimated Partnership Cost",
        desc: "Expected creator pricing range.",
    },
    {
        icon: FlaskConical,
        label: "Estimated Trials or Demos",
        desc: "Projected conversions from performance signals.",
    },
    {
        icon: Users,
        label: "Estimated Customers",
        desc: "Expected acquisition from projected trials.",
    },
];

const StepCard = ({ n, icon: Icon, title, body, focal }) => (
    <div
        data-testid={`predict-step-${n}`}
        className={`group flex-1 min-w-0 rounded-2xl p-5 md:p-6 transition-all flex flex-col ${
            focal
                ? "bg-white border-2 border-[#0A0A0A] shadow-[0_24px_60px_-34px_rgba(0,0,0,0.5)]"
                : "bg-white border border-black/[0.08] hover:border-black/20"
        }`}
    >
        <div className="flex items-center justify-between">
            <span
                className={`text-[11px] font-bold tracking-[0.2em] uppercase ${
                    focal ? "text-[#0A0A0A]" : "text-gray-400"
                }`}
            >
                {n}
            </span>
            {focal && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0A0A] px-2.5 py-1 text-[10px] font-bold tracking-[0.12em] uppercase text-[#C6F432]">
                    <span className="h-1 w-1 rounded-full bg-[#C6F432]" />
                    Focal
                </span>
            )}
        </div>
        <div
            className={`mt-5 inline-flex items-center justify-center h-11 w-11 rounded-xl transition-colors ${
                focal
                    ? "bg-[#0A0A0A]"
                    : "bg-[#FAFAFA] border border-black/[0.06] group-hover:bg-[#0A0A0A]"
            }`}
        >
            <Icon
                className={`h-5 w-5 transition-colors ${
                    focal ? "text-[#C6F432]" : "text-[#0A0A0A] group-hover:text-white"
                }`}
                strokeWidth={1.8}
            />
        </div>
        <h3 className="mt-5 font-display font-bold text-[15.5px] md:text-base tracking-tight text-[#0A0A0A] leading-[1.4]">
            {title}
        </h3>
        <p className="mt-2.5 text-[13px] text-gray-600 leading-[1.7]">{body}</p>
    </div>
);

const Connector = () => (
    <div className="flex-none flex items-center justify-center py-1 lg:py-0 lg:px-0.5">
        <ChevronDown className="h-4 w-4 text-gray-300 lg:hidden" strokeWidth={2.2} />
        <ChevronRight className="hidden lg:block h-4 w-4 text-gray-300" strokeWidth={2.2} />
    </div>
);

export const PredictBeforePartner = () => (
    <section
        id="predict"
        data-testid="predict-section"
        className="py-24 md:py-32 bg-white border-t border-black/[0.06]"
    >
        <div className="container-page">
            <div className="max-w-3xl">
                <FadeUp>
                    <p className="text-xs font-bold tracking-[0.22em] uppercase text-gray-500">
                        Predict Before We Partner
                    </p>
                </FadeUp>
                <FadeUp delay={0.05}>
                    <h2
                        data-testid="predict-heading"
                        className="mt-4 font-display font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.3] text-[#0A0A0A] text-balance"
                    >
                        We don&apos;t guess which creators will perform. We model the opportunity before outreach.
                    </h2>
                </FadeUp>
                <FadeUp delay={0.1}>
                    <p className="mt-5 text-[15.5px] md:text-base text-gray-600 leading-[1.75] max-w-2xl">
                        Every potential partnership is evaluated for cost, conversion potential, and customer acquisition before we spend a dollar.
                    </p>
                </FadeUp>
            </div>

            {/* Workflow */}
            <FadeUp delay={0.1}>
                <div className="mt-14 flex flex-col lg:flex-row lg:items-stretch gap-2 lg:gap-1.5">
                    {steps.map((s, i) => (
                        <Fragment key={s.n}>
                            <StepCard {...s} />
                            {i < steps.length - 1 && <Connector />}
                        </Fragment>
                    ))}
                </div>
            </FadeUp>

            {/* Focal: Predictive Performance Model */}
            <FadeUp delay={0.08}>
                <div
                    id="predict-model"
                    data-testid="predict-model-panel"
                    className="relative mt-5 rounded-3xl bg-[#0A0A0A] text-white p-6 md:p-10 grain-overlay overflow-hidden"
                >
                    <div className="relative flex flex-wrap items-start justify-between gap-4">
                        <div className="max-w-xl">
                            <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-[#C6F432]">
                                Step 03 · Predictive Performance Model
                            </p>
                            <h3 className="mt-3 font-display font-bold text-xl md:text-2xl tracking-tight text-white leading-[1.35]">
                                The model that runs before we spend a dollar.
                            </h3>
                            <p className="mt-3 text-[14px] text-white/60 leading-[1.7]">
                                Three modeled outputs for every creator, shown as ranges based on available signals and campaign assumptions, never guarantees.
                            </p>
                        </div>
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/10 px-3 py-1.5 text-[10.5px] font-bold tracking-[0.15em] uppercase text-white/70">
                            Estimates, not promises
                        </span>
                    </div>

                    <div className="relative mt-8 flex flex-col md:flex-row md:items-stretch gap-3 md:gap-2">
                        {metrics.map((m, i) => (
                            <Fragment key={m.label}>
                                <div
                                    data-testid={`predict-metric-${i}`}
                                    className="flex-1 min-w-0 rounded-2xl bg-white/[0.05] border border-white/10 p-5 md:p-6"
                                >
                                    <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-[#C6F432]">
                                        <m.icon className="h-5 w-5 text-[#0A0A0A]" strokeWidth={1.9} />
                                    </div>
                                    <div className="mt-4 font-display font-bold text-[15px] md:text-base tracking-tight text-white leading-[1.35]">
                                        {m.label}
                                    </div>
                                    <p className="mt-2 text-[13px] text-white/55 leading-[1.65]">
                                        {m.desc}
                                    </p>
                                    <div className="mt-4 flex items-center gap-2">
                                        <span className="h-1.5 flex-1 rounded-full bg-gradient-to-r from-white/10 via-[#C6F432]/60 to-[#C6F432]" />
                                        <span className="text-[10.5px] font-semibold tracking-[0.08em] uppercase text-white/45 whitespace-nowrap">
                                            Range
                                        </span>
                                    </div>
                                </div>
                                {i < metrics.length - 1 && (
                                    <div className="flex-none flex items-center justify-center py-1 md:py-0">
                                        <ArrowDown className="h-4 w-4 text-white/30 md:hidden" strokeWidth={2.2} />
                                        <ArrowRight className="hidden md:block h-4 w-4 text-white/30" strokeWidth={2.2} />
                                    </div>
                                )}
                            </Fragment>
                        ))}
                    </div>
                </div>
            </FadeUp>

            {/* Feedback loop */}
            <FadeUp delay={0.06}>
                <div
                    data-testid="predict-feedback-loop"
                    className="mt-5 rounded-2xl border border-dashed border-black/15 bg-[#FAFAFA] px-5 py-4 md:px-6 md:py-5 flex items-start gap-4"
                >
                    <div className="flex-none inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white border border-black/[0.08]">
                        <RefreshCw className="h-5 w-5 text-[#0A0A0A]" strokeWidth={1.8} />
                    </div>
                    <div>
                        <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-gray-500">
                            Feedback loop
                        </p>
                        <p className="mt-1 text-[14px] text-gray-700 leading-[1.7] max-w-3xl">
                            Actual campaign results flow back into the algorithm, sharpening every future cost, trial, and customer estimate over time.
                        </p>
                    </div>
                </div>
            </FadeUp>
        </div>
    </section>
);

export default PredictBeforePartner;
